import React, { useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  ExpressCheckoutElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import Cookies from "universal-cookie";
import { useNavigate } from "react-router-dom";
import CompletedModal from "../global/CompletedModal";
import {
  updateDocument,
  updateProductLimit,
} from "../../firebase/firebaseCrud";
import { useCart } from "../../context/cart-context";
import CustomButton from "../Form/CustomButton";
import { formatAmount } from "../../utils/utils";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_TEST_PUBLIC_KEY);

function CheckoutInner({ totalPrice, clientSecret, orderIdFromServer }) {
  const stripe = useStripe();
  const elements = useElements();

  const [view, setView] = useState(false);
  const [orderID, setOrderID] = useState(orderIdFromServer || "");
  const [loading, setLoading] = useState(false);

  const { cart, clearCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    if (!view) return;
    const timer = setTimeout(() => {
      clearCart();
      setOrderID("");
      setView(false);
      navigate("/dashboard");
    }, 5000);
    return () => clearTimeout(timer);
  }, [view, clearCart, navigate]);

  const handleCloseOperation = () => {
    clearCart();
    setOrderID("");
    setView(false);
    navigate("/dashboard");
  };

  const updateProductLimitsInCart = async (cartItems) => {
    for (const cartItem of cartItems) {
      await updateProductLimit(cartItem.id, cartItem.quantity);
    }
  };

  const finalizeSuccess = async (paymentIntent) => {
    setOrderID((prev) => prev || orderIdFromServer);
    setView(true);

    await updateDocument("transactions", orderIdFromServer, {
      status: "paid",
      transaction_id: paymentIntent.id,
      purchase_time: new Date().toISOString(),
      amount: (paymentIntent.amount / 100).toFixed(2),
      currency: paymentIntent.currency.toUpperCase(),
    });

    await updateProductLimitsInCart(cart);
  };

  const handlePayNow = async () => {
    if (!stripe || !elements) return;

    setLoading(true);
    try {
      // Payment Element confirmation supports multiple payment methods
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          // required for redirect-based methods like bank payment flows
          return_url: `${window.location.origin}/checkout/complete?orderId=${orderIdFromServer}`,
        },
        redirect: "if_required",
      });

      if (error) throw new Error(error.message);

      // If no redirect happened, we get a paymentIntent immediately
      if (paymentIntent?.status === "succeeded") {
        await finalizeSuccess(paymentIntent);
      } else if (paymentIntent?.status) {
        throw new Error(`Payment not completed: ${paymentIntent.status}`);
      } else {
        // Some methods will redirect and complete on return_url
        // In that case you’ll finalize on the return page or via webhook
      }
    } catch (e) {
      console.error(e);
      alert(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Optional: shows Apple Pay / Google Pay style buttons when available */}
      <div style={{ marginBottom: 12 }}>
        <ExpressCheckoutElement
          onConfirm={async () => {
            // Express checkout uses same confirmPayment call
            await handlePayNow();
          }}
        />
      </div>

      {/* Main multi-method UI, includes cards, revolut pay, bank, etc when eligible */}
      <div style={{ padding: 12, border: "1px solid #ddd", borderRadius: 8 }}>
        <PaymentElement />
      </div>

      {/* <button
        onClick={handlePayNow}
        disabled={!stripe || loading}
        style={{ marginTop: 12 }}
      >
        {loading ? "Processing..." : `Pay ${totalPrice}`}
      </button> */}

      {/* <CustomButton
        type="submit"
        // form="add-member-form"
        //   loading={true}
        sx={{ width: "200px", height: "55px" }}
        color="secondary"
        className="!text-base !uppercase !mt-10"
        onClick={handlePayNow}
        disabled={!stripe || loading}
        style={{ marginTop: 12 }}
      >
        {loading ? "Processing..." : `Pay ${totalPrice}`}
      </CustomButton> */}

      <CustomButton
        type="submit"
        // form="add-member-form"
        // loading={true}
        // sx={{ width: "220px", height: "55px" }}
        color="secondary"
        className="text-sm"
        onClick={handlePayNow}
        // to={"/checkout"}
        fullWidth
      >
        {loading ? "Processing..." : `Pay ${formatAmount(totalPrice)}`}
      </CustomButton>

      {view && (
        <CompletedModal
          modalstate={view}
          handleTerminate={handleCloseOperation}
          label="Transaction Successful"
          text={`Order ID: ${orderID}`}
          statement="Thank you for choosing us!. Enjoy your product and feel free to reach out if you have any questions or need assistance."
        />
      )}
    </>
  );
}

export default function StripeButtonComponent({ totalPrice }) {
  const { cart } = useCart();
  const [clientSecret, setClientSecret] = useState("");
  const [orderId, setOrderId] = useState("");

  const cookies = new Cookies();
  const initialToken = cookies.get("strandz-user-tokenID") || null;
  const shippingFee = Number(process.env.REACT_APP_SHIPPING_FEE) || 12;

  useEffect(() => {
    if (!cart?.length) {
      setClientSecret("");
      setOrderId("");
      return;
    }

    const controller = new AbortController();

    (async () => {
      try {
        const res = await fetch(
          "https://server.rentnow.com.ng/strandz/stripe/payment/cart",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            signal: controller.signal,
            body: JSON.stringify({
              cart,
              shippingFee, // don’t hardcode 12
              userId: initialToken,
            }),
          },
        );

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to create payment");

        setClientSecret(data.clientSecret);
        setOrderId(data.orderId);
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error(e);
          alert(e.message);
        }
      }
    })();

    return () => controller.abort();
  }, [cart, initialToken, shippingFee]);

  const options = useMemo(() => {
    if (!clientSecret) return undefined;
    return {
      clientSecret,
      appearance: { theme: "stripe" },
    };
  }, [clientSecret]);

  if (!clientSecret || !options) return null;

  return (
    <Elements
      stripe={stripePromise}
      options={options}
      key={clientSecret} // forces a remount whenever clientSecret changes
    >
      <CheckoutInner
        totalPrice={totalPrice}
        clientSecret={clientSecret}
        orderIdFromServer={orderId}
      />
    </Elements>
  );
}
